/**
 * Top-level router (createBrowserRouter, data-router / library mode).
 *
 * Public auth routes are outside <AuthGate>; everything else is gated behind
 * <AuthGate> → <AppLayout>. Every section route renders SectionPlaceholder in
 * this phase; each is replaced by a real screen in its own later spec, keeping
 * the same path and heading key.
 */
import { Navigate, createBrowserRouter, type RouteObject } from 'react-router-dom';
import { RootLayout } from '@/routes/RootLayout';
import { AppLayout } from '@/components/AppLayout';
import { AuthGate } from '@/auth/AuthGate';
import { AuthReturn } from '@/routes/AuthReturn';
import { NoAccess } from '@/routes/NoAccess';
import { SignedOut } from '@/routes/SignedOut';
import { NotFound } from '@/routes/NotFound';
import { RouteError } from '@/routes/RouteError';
import { SectionPlaceholder } from '@/routes/SectionPlaceholder';

/** Exported as a test seam so `router.test.tsx` can mount the same table
 * through `createMemoryRouter` — same pattern as `authStore.__resetAuthStore`
 * and `bootstrap.__resetBootstrap`. Not consumed anywhere else. */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      // Public auth routes — no AuthGate.
      { path: 'auth/return', element: <AuthReturn /> },
      { path: 'auth/no-access', element: <NoAccess /> },
      { path: 'auth/signed-out', element: <SignedOut /> },

      // Everything else is gated behind AuthGate → AppLayout.
      {
        element: <AuthGate />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { index: true, element: <Navigate to="/dashboard" replace /> },

              {
                path: 'dashboard',
                element: (
                  <SectionPlaceholder titleKey="page.dashboard.title" titleFallback="Dashboard" />
                ),
              },
              {
                path: 'queue',
                element: <SectionPlaceholder titleKey="page.queue.title" titleFallback="Queue" />,
              },
              {
                path: 'notifications',
                element: (
                  <SectionPlaceholder
                    titleKey="page.notifications.title"
                    titleFallback="Notifications"
                  />
                ),
              },
              {
                path: 'logs',
                element: <SectionPlaceholder titleKey="page.logs.title" titleFallback="Logs" />,
              },
              {
                path: 'tenants',
                element: (
                  <SectionPlaceholder titleKey="page.tenants.title" titleFallback="Tenants" />
                ),
              },
              {
                path: 'engine',
                element: <SectionPlaceholder titleKey="page.engine.title" titleFallback="Engine" />,
              },
              {
                path: 'global-config',
                element: (
                  <SectionPlaceholder
                    titleKey="page.globalConfig.title"
                    titleFallback="Global config"
                  />
                ),
              },

              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
